import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Get current supplier profile
export const getCurrentSupplier = async () => {
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    ?.from('suppliers')
    ?.select(`
      *,
      country:countries(id, name_en, name_zh, code)
    `)
    ?.eq('user_id', user?.id)
    ?.maybeSingle();

  if (error) throw error;
  return data ? toCamelCase(data) : null;
};

// Get supplier analytics for date range
export const getSupplierAnalytics = async (supplierId, startDate, endDate) => {
  const { data, error } = await supabase
    ?.from('supplier_analytics')
    ?.select('*')
    ?.eq('supplier_id', supplierId)
    ?.gte('period_start', startDate)
    ?.lte('period_end', endDate)
    ?.order('period_start', { ascending: false });

  if (error) throw error;
  return toCamelCase(data);
};

// Calculate real-time metrics
export const calculateSupplierMetrics = async (supplierId, startDate, endDate) => {
  const { data, error } = await supabase
    ?.rpc('calculate_supplier_metrics', {
      p_supplier_id: supplierId,
      p_start_date: startDate,
      p_end_date: endDate
    });

  if (error) throw error;
  return toCamelCase(data?.[0] || {});
};

// Get supplier reviews
export const getSupplierReviews = async (supplierId) => {
  const { data, error } = await supabase
    ?.from('supplier_reviews')
    ?.select(`
      *,
      buyer:user_profiles!buyer_id(id, full_name, company_name, country),
      communication:buyer_communications(id, subject, bean_id)
    `)
    ?.eq('supplier_id', supplierId)
    ?.eq('is_active', true)
    ?.order('created_at', { ascending: false });

  if (error) throw error;
  return toCamelCase(data);
};

// Respond to review
export const respondToReview = async (reviewId, response) => {
  const { data, error } = await supabase
    ?.from('supplier_reviews')
    ?.update({
      supplier_response: response,
      responded_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString()
    })
    ?.eq('id', reviewId)
    ?.select()
    ?.single();

  if (error) throw error;
  return toCamelCase(data);
};

// Get supplier inquiries (orders)
export const getSupplierInquiries = async (supplierId, startDate, endDate) => {
  const { data: supplier } = await supabase
    ?.from('suppliers')
    ?.select('id')
    ?.eq('id', supplierId)
    ?.single();

  if (!supplier) throw new Error('Supplier not found');

  let query = supabase
    ?.from('inquiries')
    ?.select(`
      *,
      buyer:user_profiles!buyer_id(id, full_name, company_name, country),
      bean:beans!bean_id(
        id, name_en, name_zh, price_usd_per_kg, supplier_id,
        country:countries(name_en, name_zh)
      )
    `)
    ?.eq('bean.supplier_id', supplierId);

  if (startDate) {
    query = query?.gte('created_at', startDate);
  }
  if (endDate) {
    query = query?.lte('created_at', endDate);
  }

  const { data, error } = await query?.order('created_at', { ascending: false });

  if (error) throw error;
  return toCamelCase(data);
};

// Get supplier beans with sales data
export const getSupplierBeans = async (supplierId) => {
  const { data, error } = await supabase
    ?.from('beans')
    ?.select(`
      *,
      country:countries(name_en, name_zh, code),
      farm:farms(id, name_en, name_zh),
      images:bean_images(id, image_url, alt_text_en, is_primary)
    `)
    ?.eq('supplier_id', supplierId)
    ?.eq('is_active', true)
    ?.order('created_at', { ascending: false });

  if (error) throw error;
  return toCamelCase(data);
};

// Get revenue trends by date
export const getRevenueTrends = async (supplierId, startDate, endDate) => {
  const inquiries = await getSupplierInquiries(supplierId, startDate, endDate);
  
  // Group by date
  const trends = {};
  inquiries?.forEach(inquiry => {
    const date = new Date(inquiry?.createdAt)?.toISOString()?.split('T')?.[0];
    if (!trends?.[date]) {
      trends[date] = { date, revenue: 0, orders: 0 };
    }
    if (inquiry?.status === 'completed') {
      trends[date].revenue += (inquiry?.quantityKg || 0) * (inquiry?.bean?.priceUsdPerKg || 0);
      trends[date].orders += 1;
    }
  });

  return Object.values(trends)?.sort((a, b) => new Date(a?.date) - new Date(b?.date));
};

// Get geographic distribution of buyers
export const getBuyerGeographicDistribution = async (supplierId) => {
  const inquiries = await getSupplierInquiries(supplierId);
  
  const distribution = {};
  inquiries?.forEach(inquiry => {
    const country = inquiry?.buyer?.country || 'Unknown';
    if (!distribution?.[country]) {
      distribution[country] = { country, count: 0, revenue: 0 };
    }
    distribution[country].count += 1;
    if (inquiry?.status === 'completed') {
      distribution[country].revenue += (inquiry?.quantityKg || 0) * (inquiry?.bean?.priceUsdPerKg || 0);
    }
  });

  return Object.values(distribution)?.sort((a, b) => b?.revenue - a?.revenue);
};

// Get popular beans by sales
export const getPopularBeans = async (supplierId) => {
  const beans = await getSupplierBeans(supplierId);
  const inquiries = await getSupplierInquiries(supplierId);
  
  const beanSales = {};
  inquiries?.forEach(inquiry => {
    const beanId = inquiry?.bean?.id;
    if (!beanId) return;
    
    if (!beanSales?.[beanId]) {
      const bean = beans?.find(b => b?.id === beanId);
      beanSales[beanId] = {
        bean: bean,
        totalOrders: 0,
        totalRevenue: 0,
        totalQuantityKg: 0
      };
    }
    
    if (inquiry?.status === 'completed') {
      beanSales[beanId].totalOrders += 1;
      beanSales[beanId].totalRevenue += (inquiry?.quantityKg || 0) * (inquiry?.bean?.priceUsdPerKg || 0);
      beanSales[beanId].totalQuantityKg += inquiry?.quantityKg || 0;
    }
  });

  return Object.values(beanSales)?.sort((a, b) => b?.totalRevenue - a?.totalRevenue)?.slice(0, 10);
};

// Get communications with buyers
export const getSupplierCommunications = async (supplierId) => {
  const { data, error } = await supabase
    ?.from('buyer_communications')
    ?.select(`
      *,
      buyer:user_profiles!buyer_id(id, full_name, company_name, email),
      bean:beans!bean_id(id, name_en, name_zh),
      messages:buyer_communication_messages(
        id, message, created_at,
        sender:user_profiles!sender_id(id, full_name)
      )
    `)
    ?.eq('supplier_id', supplierId)
    ?.order('created_at', { ascending: false });

  if (error) throw error;
  return toCamelCase(data);
};

export default {
  getCurrentSupplier,
  getSupplierAnalytics,
  calculateSupplierMetrics,
  getSupplierReviews,
  respondToReview,
  getSupplierInquiries,
  getSupplierBeans,
  getRevenueTrends,
  getBuyerGeographicDistribution,
  getPopularBeans,
  getSupplierCommunications
};